/**
 * Microphone capture pipeline: getUserMedia -> AudioWorklet -> callbacks.
 *
 * The engine owns every disposable resource (media stream, context, node,
 * listeners) and never touches UI state directly.
 */

import workletUrl from './tuner-processor.worklet.ts?worker&url'
import { assertCaptureSupported, constraintsFor, listAudioInputs } from './devices'
import type { AudioInputDevice } from './devices'
import { AudioEngineError, messageFor, toAudioEngineError } from './errors'
import { createTracker, type StringReading, type StringTracker } from './string-tracker'
import type { TrackerOptions } from './string-tracker'
import {
  TUNER_PROCESSOR,
  isBlockMessage,
  isLevelMessage,
  isTimingMessage,
  type TimingMessage,
  type TunerProcessorOptions,
} from './messages'

/** Input level reading from the audio thread. */
export interface LevelReading {
  /** RMS since the previous reading, in [0, 1]. */
  readonly rms: number
  /** Largest absolute sample since the previous reading, in [0, 1]. */
  readonly peak: number
}

/** Callbacks the engine invokes while running. */
export interface EngineHandlers {
  /** Level meter update, roughly `levelRateHz` times per second. */
  readonly onLevel: (level: LevelReading) => void
  /**
   * One reading per string, in tuning order, roughly every 11 ms.
   *
   * Empty until {@link AudioEngine.setTargets} has been called: without
   * targets there is nothing to measure against.
   */
  readonly onReadings?: (readings: readonly StringReading[]) => void
  /** One-shot performance report from the audio thread. */
  readonly onTiming?: (timing: TimingMessage) => void
  /** Asynchronous failure after a successful start, e.g. the device vanished. */
  readonly onError?: (error: AudioEngineError) => void
  /**
   * The pipeline stopped delivering audio without failing, or started again.
   *
   * A phone call, Siri, another app taking the microphone, the screen locking:
   * iOS suspends the context or mutes the track and expects the page to notice.
   * Nothing is broken and nothing needs restarting — but the meters are frozen
   * on their last reading, and saying so is the difference between "paused"
   * and "this tuner is lying to me".
   */
  readonly onInterruption?: (interrupted: boolean) => void
}

/** What the engine actually opened. */
export interface StartResult {
  /** deviceId of the opened input, or null when the browser did not report one. */
  readonly deviceId: string | null
  /** Inputs visible now that labels are unlocked by the granted permission. */
  readonly devices: readonly AudioInputDevice[]
  /** Sample rate the context settled on. */
  readonly sampleRate: number
}

/** Engine configuration. */
export interface EngineOptions {
  /** How the string tracker is set up; see `probe-plan.ts`. */
  readonly tracker: TrackerOptions
  /**
   * Decimated samples per block from the worklet.
   *
   * At the ~12 kHz analysis rate 128 samples is about 11 ms, which is how
   * often a needle can move.
   */
  readonly blockSize: number
  /** Target level messages per second. */
  readonly levelRateHz: number
  /** Blocks the worklet profiles before reporting timing once. */
  readonly timingBlocks: number
}

/** Defaults: a reading every ~11 ms, 30 level updates per second. */
export const DEFAULT_ENGINE_OPTIONS: EngineOptions = {
  tracker: {},
  blockSize: 128,
  levelRateHz: 30,
  timingBlocks: 4000,
}

export { AudioEngineError }
export type { AudioErrorKind } from './errors'

/** Captures microphone audio and reports levels and detected pitches. */
export class AudioEngine {
  private context: AudioContext | null = null
  private stream: MediaStream | null = null
  private node: AudioWorkletNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private tracker: StringTracker | null = null
  /** Target frequencies the tracker was built for, in Hz. */
  private targets: readonly number[] = []
  /** Rate the current tracker was built for, in Hz. */
  private trackerRate = 0
  private handlers: EngineHandlers | null = null
  private visibilityListener: (() => void) | null = null
  /** Last reported interruption state, so nothing is reported twice. */
  private interrupted = false

  constructor(private readonly options: EngineOptions = DEFAULT_ENGINE_OPTIONS) {}

  /** True while the microphone is running. */
  get isRunning(): boolean {
    return this.node !== null
  }

  /** Actual sample rate of the running context, or null when stopped. */
  get sampleRate(): number | null {
    return this.context?.sampleRate ?? null
  }

  /** Current context state, or null when stopped. */
  get contextState(): AudioContextState | null {
    return this.context?.state ?? null
  }

  /**
   * Open the microphone and start analysing.
   *
   * Must be called synchronously from a user gesture: the AudioContext is
   * constructed before the first `await` so Safari and iOS still consider the
   * gesture active. Everything after that is allowed to be asynchronous.
   *
   * @param deviceId - Preferred input, or null to let the browser choose.
   * @throws {AudioEngineError} Classified failure; nothing is left running.
   */
  async start(deviceId: string | null, handlers: EngineHandlers): Promise<StartResult> {
    if (this.isRunning) {
      throw new AudioEngineError('unknown', 'Der Tuner läuft bereits.')
    }

    assertCaptureSupported()

    // Constructed inside the gesture, before any await.
    const context = new AudioContext({ latencyHint: 'interactive' })
    this.context = context
    this.handlers = handlers

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraintsFor(deviceId))
    } catch (error) {
      await this.stop()
      throw toAudioEngineError(error, deviceId !== null)
    }

    try {
      await context.audioWorklet.addModule(workletUrl)

      const processorOptions: TunerProcessorOptions = {
        blockSize: this.options.blockSize,
        levelRateHz: this.options.levelRateHz,
        timingBlocks: this.options.timingBlocks,
      }
      const node = new AudioWorkletNode(context, TUNER_PROCESSOR, {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        // Take however many channels the input actually provides and hand them
        // over untouched; the processor folds them to mono itself. With the
        // default `speakers` interpretation Web Audio would apply its own
        // downmix rules, which for some channel counts drop channels entirely.
        channelCountMode: 'max',
        channelInterpretation: 'discrete',
        processorOptions,
      })
      node.port.onmessage = (event: MessageEvent<unknown>) => {
        this.handleMessage(event.data)
      }

      this.source = context.createMediaStreamSource(this.stream)
      this.source.connect(node)
      this.node = node

      this.watchTrack()
      this.watchVisibility()
      this.watchInterruptions(context)
      await this.resume()

      return {
        deviceId: this.openedDeviceId(),
        devices: await listAudioInputs(),
        sampleRate: context.sampleRate,
      }
    } catch (error) {
      await this.stop()
      throw error instanceof AudioEngineError
        ? error
        : new AudioEngineError('unknown', messageFor('unknown'), error)
    }
  }

  /** True while the browser has paused the pipeline without failing it. */
  get isInterrupted(): boolean {
    return this.interrupted
  }

  /**
   * Tell the engine which frequencies to measure against.
   *
   * Safe to call at any time, running or not: changing the tuning or the
   * concert pitch moves every target, and the probe bank is rebuilt around
   * the new ones. Calling it with the same numbers is free, which matters
   * because the settings that produce them re-render for other reasons.
   *
   * @param targets - Open-string frequencies in Hz, low string first.
   */
  setTargets(targets: readonly number[]): void {
    const unchanged =
      targets.length === this.targets.length &&
      targets.every((target, i) => Math.abs(target - this.targets[i]!) < 1e-9)
    if (unchanged) return

    this.targets = [...targets]
    // Dropped rather than rebuilt here: the rate comes from the worklet, and
    // the next block carries it.
    this.tracker = null
    this.trackerRate = 0
  }

  /**
   * Resume a context the browser suspended, e.g. after a tab switch or an
   * interruption on iOS. Safe to call when already running.
   *
   * Call it from a user gesture where possible: after some interruptions iOS
   * only honours a resume that a tap asked for.
   */
  async resume(): Promise<void> {
    const context = this.context
    if (context?.state !== 'suspended') return
    try {
      await context.resume()
      // Re-read through the field: the engine may have been stopped while the
      // resume was in flight, in which case there is nothing to report.
      const state = this.context?.state
      if (state !== undefined) this.reportInterruption(state !== 'running')
    } catch (error) {
      this.reportAsyncError(new AudioEngineError('unknown', 'Audio could not be resumed.', error))
    }
  }

  /** Stop analysing and release the microphone. */
  async stop(): Promise<void> {
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener)
      this.visibilityListener = null
    }
    if (this.context) {
      // Cleared before closing: closing moves the context out of `running`,
      // which is otherwise exactly the signal for an interruption.
      this.context.onstatechange = null
    }
    this.interrupted = false
    if (this.node) {
      this.node.port.onmessage = null
      this.node.disconnect()
      this.node = null
    }
    if (this.source) {
      this.source.disconnect()
      this.source = null
    }
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.onended = null
        track.onmute = null
        track.onunmute = null
        track.stop()
      }
      this.stream = null
    }
    if (this.context) {
      await this.context.close()
      this.context = null
    }
    this.tracker = null
    this.trackerRate = 0
    this.handlers = null
  }

  /**
   * Tracker matching the rate of the incoming blocks.
   *
   * The analysis path runs decimated, and the worklet reports the rate it
   * settled on. Building from that rather than from the AudioContext keeps
   * the two from drifting apart, which would silently misplace every probe.
   */
  private trackerFor(rate: number): StringTracker | null {
    if (this.targets.length === 0) return null
    if (!this.tracker || this.trackerRate !== rate) {
      this.tracker = createTracker(this.targets, rate, this.options.tracker)
      this.trackerRate = rate
    }
    return this.tracker
  }

  /** deviceId the browser actually opened, from the live track settings. */
  private openedDeviceId(): string | null {
    const settings = this.stream?.getAudioTracks()[0]?.getSettings()
    return settings?.deviceId ?? null
  }

  /**
   * Report when the input disappears mid-session (interface unplugged), and
   * when it merely goes quiet.
   *
   * `mute` is not `ended`: the track is still there, it just carries silence
   * because something else took priority — a call on iOS, another app opening
   * the microphone. It comes back by itself, so this is a status, not an error.
   */
  private watchTrack(): void {
    const track = this.stream?.getAudioTracks()[0]
    if (!track) return
    track.onended = () => {
      this.reportAsyncError(new AudioEngineError('device-gone', messageFor('device-gone')))
    }
    track.onmute = () => {
      this.reportInterruption(true)
    }
    track.onunmute = () => {
      this.reportInterruption(false)
    }
  }

  /**
   * Watch the context for the browser pausing it underneath us.
   *
   * On iOS an interruption suspends the AudioContext outright; `process()`
   * simply stops being called, with no error anywhere.
   */
  private watchInterruptions(context: AudioContext): void {
    context.onstatechange = () => {
      if (this.node === null) return
      this.reportInterruption(context.state !== 'running')
    }
  }

  /** Pass an interruption on once, when it actually changes. */
  private reportInterruption(interrupted: boolean): void {
    if (this.interrupted === interrupted) return
    this.interrupted = interrupted
    this.handlers?.onInterruption?.(interrupted)
  }

  /**
   * Browsers suspend the context in a hidden tab and, on iOS, after phone
   * calls or route changes. Resume as soon as the page is visible again.
   */
  private watchVisibility(): void {
    const listener = (): void => {
      if (document.visibilityState === 'visible') {
        void this.resume()
      }
    }
    this.visibilityListener = listener
    document.addEventListener('visibilitychange', listener)
  }

  private reportAsyncError(error: AudioEngineError): void {
    this.handlers?.onError?.(error)
  }

  private handleMessage(data: unknown): void {
    const handlers = this.handlers
    if (!handlers) return

    if (isLevelMessage(data)) {
      handlers.onLevel({ rms: data.rms, peak: data.peak })
      return
    }
    if (isTimingMessage(data)) {
      handlers.onTiming?.(data)
      return
    }
    if (isBlockMessage(data) && handlers.onReadings) {
      try {
        const tracker = this.trackerFor(data.sampleRate)
        if (!tracker) return
        tracker.push(data.samples)
        handlers.onReadings(tracker.read())
      } catch (error) {
        console.error('[multitimbral] block analysis failed', error)
      }
    }
  }
}
